import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { renameFolder } from "../api/folders.api";
import type {
  FolderSummary,
  StorageErrorCode,
} from "../types/project-storage.types";
import { mapStorageError } from "../utils/storageErrors";
import { folderChildrenQueryKey } from "./useFolderChildren";
import { folderTreeQueryKey } from "./useFolderTree";

export type RenameFolderResult =
  | { ok: true; folder: FolderSummary }
  | { ok: false; errorCode?: StorageErrorCode; errorMessage: string };

export function useRenameFolder(
  mmCode: number | null,
  folderId: string | null
) {
  const queryClient = useQueryClient();
  const [isRenaming, setIsRenaming] = useState(false);

  const renameFolderItem = useCallback(
    async (displayName: string): Promise<RenameFolderResult> => {
      if (mmCode === null || !folderId) {
        return {
          ok: false,
          errorMessage: "Não foi possível renomear a pasta.",
        };
      }

      setIsRenaming(true);

      try {
        const folder = await renameFolder(mmCode, folderId, { displayName });

        await Promise.all([
          queryClient.invalidateQueries(folderTreeQueryKey(mmCode)),
          folderId
            ? queryClient.invalidateQueries(folderChildrenQueryKey(mmCode, folderId))
            : Promise.resolve(),
        ]);

        toast.success("Pasta renomeada com sucesso.");
        return { ok: true, folder };
      } catch (error) {
        const mapped = mapStorageError(
          error,
          "action",
          "Não foi possível renomear a pasta."
        );

        return {
          ok: false,
          errorCode: mapped.errorCode,
          errorMessage: mapped.message,
        };
      } finally {
        setIsRenaming(false);
      }
    },
    [folderId, mmCode, queryClient]
  );

  return {
    renameFolderItem,
    isRenaming,
  };
}
